import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Check,
  Clock,
  MessageCircle,
  Shield,
  X,
} from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  Loading,
} from '@/components/ui';
import { useAccessRequestStore, type AccessRequest } from '@/stores/accessRequestStore';
import { useMessageStore } from '@/stores/messageStore';
import { useAuthStore } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';
import { formatRelativeTime } from '@kulum/shared';

type TabType = 'pending' | 'approved' | 'rejected';

export function AccessRequestsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    requests,
    isLoading,
    error,
    fetchReceivedRequests,
    approveRequest,
    rejectRequest,
  } = useAccessRequestStore();
  const { findOrCreateConversation } = useMessageStore();
  const { addToast } = useToastStore();

  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [startingChatId, setStartingChatId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.uid) {
      void fetchReceivedRequests(user.uid);
    }
  }, [user?.uid, fetchReceivedRequests]);

  const handleApprove = async (requestId: string) => {
    try {
      setProcessingId(requestId);
      await approveRequest(requestId);
      addToast('success', '申請を承認しました');
    } catch {
      addToast('error', '承認に失敗しました');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      setProcessingId(requestId);
      await rejectRequest(requestId);
      addToast('success', '申請を拒否しました');
    } catch {
      addToast('error', '拒否に失敗しました');
    } finally {
      setProcessingId(null);
    }
  };

  const handleStartChat = async (otherUserId: string) => {
    if (!user?.uid) return;

    try {
      setStartingChatId(otherUserId);
      const conversationId = await findOrCreateConversation(user.uid, otherUserId);
      navigate('/messages', { state: { openConversationId: conversationId } });
    } catch {
      addToast('error', 'メッセージを開始できませんでした');
    } finally {
      setStartingChatId(null);
    }
  };

  const filteredRequests = requests.filter((req) => req.status === activeTab);

  const pendingCount = requests.filter((req) => req.status === 'pending').length;
  const approvedCount = requests.filter((req) => req.status === 'approved').length;
  const rejectedCount = requests.filter((req) => req.status === 'rejected').length;

  const tabs: { id: TabType; label: string; count: number }[] = [
    { id: 'pending', label: '新着', count: pendingCount },
    { id: 'approved', label: '承認済み', count: approvedCount },
    { id: 'rejected', label: '拒否', count: rejectedCount },
  ];

  return (
    <div className="min-h-full bg-[#F7F4EF]">
      <div className="md:mx-auto md:max-w-3xl md:px-6 md:py-6">
        <div className="px-4 pb-12 pt-14 md:px-0 md:pt-0">
          <Link
            to="/mypage"
            className="mb-5 inline-flex items-center gap-2 text-[13px] text-[#7E7668] transition hover:text-copper"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>マイページに戻る</span>
          </Link>

          <div className="mb-6 flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#B7B0A4]">Requests</p>
              <h1 className="mt-1 text-[22px] font-semibold text-[#1A1A1A]">閲覧申請管理</h1>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F5EDE2]">
              <Shield className="h-5 w-5 text-copper" />
            </div>
          </div>

          <div className="mb-5 overflow-x-auto pb-1">
            <div className="inline-flex rounded-full border border-[#E7DFD2] bg-white p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition ${
                    activeTab === tab.id
                      ? 'bg-copper text-white shadow-[0_4px_12px_rgba(184,115,51,0.28)]'
                      : 'text-[#8D877D] hover:bg-[#F7F3ED]'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span
                      className={`inline-flex min-w-[1.15rem] items-center justify-center rounded-full px-1 text-[10px] font-bold ${
                        activeTab === tab.id
                          ? 'bg-white text-copper'
                          : 'bg-[#F0EDE8] text-[#7D766C]'
                      }`}
                    >
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-12">
              <Loading />
            </div>
          )}

          {!isLoading && filteredRequests.length > 0 && (
            <div className="space-y-3">
              {filteredRequests.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onApprove={() => handleApprove(request.id)}
                  onReject={() => handleReject(request.id)}
                  onStartChat={() => handleStartChat(request.fromUserId)}
                  isProcessing={processingId === request.id}
                  isStartingChat={startingChatId === request.fromUserId}
                />
              ))}
            </div>
          )}

          {!isLoading && filteredRequests.length === 0 && (
            <Card className="overflow-hidden border-[#ECE6DD]">
              <CardContent className="py-16 text-center">
                <Shield className="mx-auto mb-4 h-14 w-14 text-neutral-300" />
                <h2 className="mb-2 text-lg font-semibold text-[#1A1A1A]">
                  {activeTab === 'pending' && '新しい閲覧申請はありません'}
                  {activeTab === 'approved' && '承認済みの申請はありません'}
                  {activeTab === 'rejected' && '拒否した申請はありません'}
                </h2>
                <p className="mx-auto max-w-md text-sm text-[#8D877D]">
                  {activeTab === 'pending' &&
                    'ドニーからの詳細プロフィール閲覧申請がここに表示されます。'}
                  {activeTab === 'approved' && '承認した申請はメッセージでやり取りができます。'}
                  {activeTab === 'rejected' && '過去に拒否した申請の履歴が表示されます。'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestCard({
  request,
  onApprove,
  onReject,
  onStartChat,
  isProcessing,
  isStartingChat,
}: {
  request: AccessRequest;
  onApprove: () => void;
  onReject: () => void;
  onStartChat: () => void;
  isProcessing: boolean;
  isStartingChat: boolean;
}) {
  const getStatusBadge = () => {
    switch (request.status) {
      case 'pending':
        return (
          <Badge variant="warning" size="sm">
            <Clock className="h-3 w-3" />
            審査待ち
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="success" size="sm">
            <Check className="h-3 w-3" />
            承認済み
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="error" size="sm">
            <X className="h-3 w-3" />
            拒否
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="overflow-hidden border-[#ECE6DD]">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar
            src={request.fromUser?.iconUrl}
            alt={request.fromUser?.displayName || 'Unknown'}
            size="sm"
          />

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="truncate font-semibold text-[#1A1A1A]">
                {request.fromUser?.displayName || 'Unknown'}
              </h3>
              {getStatusBadge()}
            </div>

            <div className="mb-2 flex items-center gap-1.5 text-sm text-[#8D877D]">
              <Calendar className="h-4 w-4" />
              <span>{formatRelativeTime(request.requestedAt)}</span>
            </div>

            {request.message && (
              <p className="mb-3 rounded-xl border border-[#EFEAE2] bg-[#FCFBF9] px-3 py-2.5 text-sm text-[#726C63]">
                {request.message}
              </p>
            )}

            {request.status === 'pending' && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="!rounded-full"
                  onClick={onApprove}
                  isLoading={isProcessing}
                  leftIcon={<Check className="h-4 w-4" />}
                >
                  承認
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="!rounded-full"
                  onClick={onReject}
                  disabled={isProcessing}
                  leftIcon={<X className="h-4 w-4" />}
                >
                  拒否
                </Button>
              </div>
            )}

            {request.status === 'approved' && (
              <Button
                variant="secondary"
                size="sm"
                className="!rounded-full"
                onClick={onStartChat}
                isLoading={isStartingChat}
                leftIcon={<MessageCircle className="h-4 w-4" />}
              >
                メッセージ
              </Button>
            )}

            {request.status === 'rejected' && request.respondedAt && (
              <p className="text-sm text-[#9B948A]">{formatRelativeTime(request.respondedAt)}に拒否</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
